import { Color } from 'foundations-theming';
import { MarginType } from 'foundations-components/types';
import { ValueOf } from './types/Utils';
import { AVATAR_TYPES, DEFAULT_AVATAR_TYPES } from './lib/resolveAvatarType';

export { AVATAR_TYPES, DEFAULT_AVATAR_TYPES };
export type { DefaultAvatarType } from './lib/resolveAvatarType';

// `minSize` is the floor (in px) each network's mark may render at, mirroring
// the minimum logo sizes declared in social network-config. Most networks floor
// at 20px; YouTube's wider mark needs 28px to stay legible.
const SOCIAL_MAP_DEFAULT_EXTRA_PROPS = {
  showBackground: false,
  minSize: 20,
} as const;

export const SOCIAL_MAP = {
  facebook: { logoName: 'facebook', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  google: { logoName: 'google', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  instagram: {
    logoName: 'instagram',
    ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS,
    showBackground: true,
  },
  linkedin: { logoName: 'linkedin', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  reddit: { logoName: 'reddit', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  slack: { logoName: 'slack', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  tiktok: { logoName: 'tiktok', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  twitter: { logoName: 'x-social', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  x: { logoName: 'x-social', ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS },
  youtube: {
    logoName: 'youtube',
    ...SOCIAL_MAP_DEFAULT_EXTRA_PROPS,
    minSize: 28,
  },
} as const;

export const AVATAR_SIZES = {
  xxs: 16,
  xs: 24,
  sm: 32,
  md: 48,
  lg: 72,
  xl: 108,
  responsive: 'responsive',
} as const;

export const AVATAR_LIST_SIZES_MARGIN_OFFSET: Record<
  keyof typeof AVATAR_SIZES,
  MarginType
> = {
  xxs: '-50',
  xs: '-100',
  sm: '-100',
  md: '-200',
  lg: '-200',
  xl: '-400',
  responsive: '0',
};

export const AVATAR_TYPE_ICONS = {
  COMPANIES: 'companies',
  CONTACT: 'contacts',
  PRODUCT: 'product',
  TICKET: 'ticket',
  DEAL: 'deals',
  MARKETING_EVENT: 'marketing-event',
} as const;

export const RETINA_MULTIPLIER = 2;
export const API_KNOWN_KEY_MAPPINGS = {
  contact: 'knownContacts',
  company: 'knownCompanies',
};

export const API_KNOWN_VALUE_MAPPINGS = {
  contact: {
    primaryIdentifier: 'vid',
    secondaryIdentifier: 'email',
  },
  company: {
    primaryIdentifier: 'companyId',
    secondaryIdentifier: 'domain',
  },
};

export const API_IDENTIFIER_MAPPINGS = {
  // vids
  contact: 'vids',
  vids: 'vids',
  vid: 'vids',

  // companyIds
  company: 'companyIds',
  companyId: 'companyIds',

  // emails
  email: 'emails',
  enrichmentEmail: 'emails',

  // domains
  domain: 'domains',
  enrichmentDomain: 'domains',

  // hubSpotUserEmails
  'ai-agent': 'hubSpotUserEmails',
  hubSpotUserEmails: 'hubSpotUserEmails',
  hubSpotUserEmail: 'hubSpotUserEmails',

  // productIds
  product: 'productIds',
  productId: 'productIds',

  // marketingEventIds
  marketingEventId: 'marketingEventIds',
};

export const DEFAULT_MAX_UPLOAD_SIZE_MB = 4;

export const OBJECT_TYPES_THAT_REQUIRE_PORTAL_ID = [
  'contact',
  'vid',
  'company',
  'companyId',
  'product',
  'productId',
  'marketingEventId',
];

export const AVATAR_SOURCE_TYPES = {
  DEFAULT_COMPANY_AVATAR: 'default-company-avatar',
  DEFAULT_CONTACT_AVATAR: 'default-contact-avatar',
  KNOWN_SRC: 'known-src',
};

export const MULTI_AVATAR_DEFAULT_BORDER_COLOR = 'border-core-on-fill-default';
export const MULTI_AVATAR_DEFAULT_BORDER_WIDTH = '200';
export const MULTI_AVATAR_DEFAULT_BORDER_STYLE = 'solid';

export const AVATAR_BACKGROUND_COLOR_THEME_KEY =
  'fill-accent-neutral-default' as const;
export const AVATAR_ICON_COLOR_THEME_KEY = 'icon-primary-default' as const;

export const TRELLIS_DEFAULT_COLORS = {
  iconColor: 'icon-core-default' as Color,
  backgroundColor: 'specialty-table-header-hover' as Color,
} as const;

export const DEFAULT_ICON_COLOR_MAP: Record<
  ValueOf<typeof DEFAULT_AVATAR_TYPES>,
  { iconColor: Color; backgroundColor: Color }
> = {
  [DEFAULT_AVATAR_TYPES.AI_AGENT]: {
    iconColor: 'breeze-icon-secondary-default' as Color,
    backgroundColor: 'fill-accent-magenta-subtle-alt',
  },
  [DEFAULT_AVATAR_TYPES.CAMPAIGN]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.CONTACT]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.COMPANIES]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.PRODUCT]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.TICKET]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.DEAL]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.MARKETING_EVENT]: TRELLIS_DEFAULT_COLORS,
} as const;

export const LOOKUP_TYPE_TO_OBJECT_TYPE_ID: Record<string, string> = {
  contact: '0-1',
  vid: '0-1',
  company: '0-2',
  companyId: '0-2',
  productId: '0-7',
  marketingEventId: '0-54',
};

export const DEFAULT_PROPS = {
  showTooltip: true,
  size: 'sm',
  childMarginInlineStart: '300',
  childTruncateLength: 200,
} as const;
